import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, ArrowRight, Award, MapPin, Scale, AlertTriangle, ShieldCheck } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import { caseResults } from '@/data/caseResults';

const CaseResultsHubPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="case-results-hub">
      <SEOHead
        noindex={true}
        title="Case Results | Personal Injury Settlements | Amy C. Brown Law"
        description="Real personal injury case results from Amy C. Brown Law: confidential six-figure settlements for medical malpractice, slip and fall, and other injury claims in Washington."
        pageType="website"
        collectionData={{
          name: 'Case Results — Amy C. Brown Law',
          description: 'Real personal injury case results across medical malpractice, slip and fall, and other injury claims in Washington State.',
          items: caseResults.map((c) => ({
            name: c.headline,
            url: c.path,
            description: c.summary
          }))
        }}
      />

      {/* Hero */}
      <section className="pt-16 pb-14 md:pt-20 md:pb-20 bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <span className="inline-block bg-teal-50 text-teal-700 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-5">
            Case Results
          </span>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-secondary leading-tight mb-5">
            Real Results for Real Injuries
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl">
            Every case is different, and past outcomes never guarantee future ones. These are examples of what dedicated preparation and thorough investigation can look like — presented with certain details generalized to protect client confidentiality.
          </p>
        </div>
      </section>

      {/* Results grid */}
      <section className="py-14 md:py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {caseResults.map((c) => (
              <Link
                key={c.slug}
                to={c.path}
                data-testid={`hub-case-card-${c.slug}`}
                className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
              >
                <div className="p-7 md:p-8 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1.5 bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-[11px] font-semibold tracking-widest uppercase">
                      <Award className="w-3 h-3" /> {c.practiceArea}
                    </span>
                  </div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-secondary group-hover:text-teal-700 transition-colors mb-3 leading-snug">
                    {c.headline}
                  </h2>
                  <p className="text-slate-600 leading-relaxed mb-5 flex-1">
                    {c.summary}
                  </p>
                  <dl className="grid grid-cols-1 gap-2 text-sm mb-5 border-t border-slate-100 pt-4">
                    <div className="flex items-start gap-2">
                      <Scale className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <div>
                        <dt className="sr-only">Result</dt>
                        <dd className="text-slate-700"><span className="font-semibold">Result:</span> {c.result}</dd>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <div>
                        <dt className="sr-only">Injury</dt>
                        <dd className="text-slate-700"><span className="font-semibold">Injury:</span> {c.injury}</dd>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <div>
                        <dt className="sr-only">Location</dt>
                        <dd className="text-slate-700"><span className="font-semibold">Location:</span> {c.location}</dd>
                      </div>
                    </div>
                  </dl>
                  <span className="inline-flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all">
                    Read the Case Study <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* Disclaimer strip */}
          <div className="mt-10 flex items-start gap-3 text-sm text-slate-600 leading-relaxed bg-white rounded-2xl border border-slate-100 p-5">
            <ShieldCheck className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" aria-hidden="true" />
            <p>
              <strong className="text-secondary">Every case is different.</strong> These results do not guarantee or predict the outcome of any other case. Certain details have been generalized or omitted to protect client confidentiality. Past results do not constitute a guarantee, warranty, or prediction regarding the outcome of your legal matter.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Do You Have a Case?
          </h2>
          <p className="text-lg md:text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Contact Amy C. Brown Law for a free consultation. You pay no attorney fee unless I recover compensation for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:425-675-1224"
              data-testid="hub-cta-call"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-8 py-4 rounded-full font-bold hover:bg-teal-50 transition shadow-lg"
            >
              <Phone className="w-5 h-5" /> Call (425) 675-1224
            </a>
            <a
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white/10 transition"
            >
              <MessageCircle className="w-5 h-5" /> Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseResultsHubPage;
